import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { X, Settings, Shield, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const CookieBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setTimeout(() => setShowBanner(true), 1500);
    } else {
      setHasConsent(true);
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
        // Restore Google Consent Mode from saved preferences
        if (typeof window.gtag === 'function') {
          window.gtag('consent', 'update', {
            'ad_storage': savedPrefs.marketing ? 'granted' : 'denied',
            'ad_user_data': savedPrefs.marketing ? 'granted' : 'denied',
            'ad_personalization': savedPrefs.marketing ? 'granted' : 'denied',
            'analytics_storage': savedPrefs.analytics ? 'granted' : 'denied'
          });
        }
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, []);

  // Update Google Consent Mode v2
  const updateGoogleConsent = (analytics, marketing) => {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': marketing ? 'granted' : 'denied',
        'ad_user_data': marketing ? 'granted' : 'denied',
        'ad_personalization': marketing ? 'granted' : 'denied',
        'analytics_storage': analytics ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    const newPreferences = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setPreferences(newPreferences);
    setHasConsent(true);
    setShowBanner(false);
    // Update Google Consent Mode - grant all
    updateGoogleConsent(true, true);
  };

  const handleAcceptNecessary = () => {
    const newPreferences = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem('cookie-consent', JSON.stringify(newPreferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setPreferences(newPreferences);
    setHasConsent(true);
    setShowBanner(false);
    // Update Google Consent Mode - deny optional
    updateGoogleConsent(false, false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setHasConsent(true);
    setShowBanner(false);
    setShowSettings(false);
    // Update Google Consent Mode based on user preferences
    updateGoogleConsent(preferences.analytics, preferences.marketing);
  };

  const handleReopenSettings = () => {
    setShowBanner(true);
    setShowSettings(true);
  };

  // Show floating button if consent given, else show banner
  if (!showBanner && hasConsent) {
    return (
      <button
        onClick={handleReopenSettings}
        className="fixed bottom-4 left-4 z-50 p-3 rounded-full bg-primary/90 hover:bg-primary text-primary-foreground shadow-lg transition-all duration-300 hover:scale-110 group"
        aria-label={t('cookie.managePreferences', 'Manage cookie preferences')}
        title={t('cookie.managePreferences', 'Manage cookie preferences')}
      >
        <Cookie className="h-5 w-5" />
        <span className="sr-only">{t('cookie.managePreferences', 'Manage cookie preferences')}</span>
      </button>
    );
  }

  if (!showBanner) return null;

  return (
    <>
      {/* Main Banner */}
      <div 
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-card border-t shadow-2xl transition-transform duration-500',
          showSettings ? 'translate-y-full' : 'translate-y-0'
        )}
        role="dialog"
        aria-label="Cookie consent"
      >
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-shrink-0">
              <div className="p-3 bg-primary/10 rounded-full">
                <Shield className="w-6 h-6 text-primary" />
              </div>
            </div>
            
            <div className="flex-1">
              <h3 className="font-semibold mb-1">{t('cookies.title')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('cookies.description')}
                <Link to="/cookie" className="text-primary hover:underline ml-1">
                  {t('cookies.learnMore')}
                </Link>
              </p>
            </div>

            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="flex-1 md:flex-none"
              >
                <Settings className="w-4 h-4 mr-2" />
                {t('cookies.customize')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAcceptNecessary}
                className="flex-1 md:flex-none"
              >
                {t('cookies.necessaryOnly')}
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="flex-1 md:flex-none"
              >
                {t('cookies.acceptAll')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card border rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">{t('cookies.preferencesTitle')}</h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSettings(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Necessary Cookies */}
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{t('cookies.necessary.title')}</h3>
                    <span className="text-xs px-2 py-1 bg-green-500/20 text-green-500 rounded">
                      {t('cookies.necessary.status')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.necessary.desc')}
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{t('cookies.analytics.title')}</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.analytics.desc')}
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{t('cookies.marketing.title')}</h3>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={(e) => setPreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                    </label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.marketing.desc')}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowSettings(false)}
                >
                  {t('cookies.cancel')}
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleSavePreferences}
                >
                  {t('cookies.savePreferences')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
